import { useNavigation } from "@react-navigation/native";
import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Image,
  Modal,
  StyleSheet,
  TouchableOpacity,
  View,
  Alert,
  Pressable,
  Keyboard,
} from "react-native";
import { TextInput } from "react-native-gesture-handler";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import t from "@constants/localization";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import Text from "@components/CustomText";
import color from "@constants/color";
import PrimaryButton from "@components/PrimaryButton";
import {
  markDelivery,
  markPickup,
  markRefunded,
  rejectDelivery,
  cancelPickupReq,
  getDeliveryOtpViaSms,
} from "@services/api";
import ToastMessage from "@components/Toast";
import { actions as rrdActions } from "@reducers/rrdReducer";
import { useDispatch, useSelector } from "react-redux";
import analytics from "@services/analytics";
import {
  eventActionMapping,
  RRD_EVENTS,
  commissionEventsProps,
} from "@constants/analyticsEventsCT";
import Success from "@assets/images/success-modal.webp";
import Cross from "@assets/svg/Cross";
import useCountDown from "react-countdown-hook";
import MetaStrip from "../../components/MetaStrip";
import useLocationHook from "../../components/common/LocationHook";
import OtpNumberModal from "./OtpNumberModal.tsx";

const PATH = "screens/RRD/OTPVerification";

function OTPVerification({ route }) {
  const {
    params: {
      action,
      id,
      otpLength,
      otpText,
      afterOtpRedirectScreenName = "",
      payable_amount,
      phone_numbers = [],
      masked_phone_numbers = [],
      cx_id = "",
    },
  } = route;
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const [otp, setOtp] = useState("");
  const [isSubmitting, setSubmitting] = useState(false);
  const getCurrentLocation = useLocationHook();
  const [showInstantCommissionModal, setShowInstantCommissionModal] =
    useState(false);
  const [commissionMsgs, setCommissionMsgs] = useState({});
  const [countDown, { start, reset }] = useCountDown(30 * 1000, 1000);
  const [isCountDownRunning, setIsCountDownRunning] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [showNumberModal, setShowNumberModal] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState("");
  const resendOtpBtnLoadingRef = useRef(false);
  const cp_db_id = useSelector(({ auth }) => auth?.user?.cp_db_id);
  const insets = useSafeAreaInsets();

  const onChange = (value) => setOtp(value);

  useEffect(() => {
    if (countDown === 0) {
      setIsCountDownRunning(false);
    } else {
      setIsCountDownRunning(true);
    }
  }, [countDown]);

  useEffect(() => {
    if (otp && otp.length === otpLength) {
      Keyboard.dismiss();
    }
  }, [otp, otpLength]);

  const resendOtp = async (phoneNumber) => {
    if (resendOtpBtnLoadingRef.current) return;
    resendOtpBtnLoadingRef.current = true;
    try {
      const phoneIndex = phone_numbers.indexOf(phoneNumber);
      const maskedNumber = masked_phone_numbers?.[phoneIndex] || phoneNumber;
      setSelectedPhoneNumber(maskedNumber);

      const response = await getDeliveryOtpViaSms(
        phoneNumber,
        id,
        payable_amount,
      );
      // if (!response?.data) {
      //   throw new Error("Response is empty");
      // }
      ToastMessage(
        "success",
        response?.data?.toastMessage || "OTP Sent Successfully",
        1500,
      );
      reset();
      start();
      setIsCountDownRunning(true);
      setShowResend(true);
      analytics.trackEvent(
        phone_numbers.length > 1
          ? phoneNumber === phone_numbers[0]
            ? RRD_EVENTS.DELIVERY_OTP_MODAL_PH1
            : RRD_EVENTS.DELIVERY_OTP_MODAL_PH2
          : RRD_EVENTS.DELIVERY_OTP_PH1,
        {
          order_id: id,
          cx_id,
          cp_db_id: cp_db_id || null,
        },
      );
    } catch (err) {
      ToastMessage("error", "Try Again", 1500);
    } finally {
      resendOtpBtnLoadingRef.current = false;
      setShowNumberModal(false);
    }
  };

  const handleVerification = async () => {
    setSubmitting(true);
    let response;
    if (action === "confirmPickup") {
      response = await markPickup(id, otp);
    } else if (action === "confirmCancelPickup") {
      response = await cancelPickupReq(id, otp);
    } else if (action === "confirmDelivery") {
      const coords = await getCurrentLocation(2);
      response = await markDelivery(id, otp, coords.latitude, coords.longitude);
    } else if (action === "confirmCancelDelivery") {
      response = await rejectDelivery(id, otp);
    } else if (action === "confirmRefund") {
      response = await markRefunded(id, otp);
    }
    if (response?.status === 200) {
      setSubmitting(false);
      dispatch(rrdActions.fetchRRDInfo());
      analytics.trackEvent(RRD_EVENTS.OTP_SUBMIT, {
        id,
        Marking_type: eventActionMapping[action],
      });
      if (response?.data?.type === "INSTANT_COMMISSION_TRIGGERED") {
        setCommissionMsgs(response?.data);
        setShowInstantCommissionModal(true);
        return;
      }
      ToastMessage("success", response?.data?.toastMessage);
      if (action === "confirmDelivery") {
        navigation.replace("OrderDetails", {
          order_id: id,
        });
        return;
      }
      navigation.goBack();
    } else {
      Alert.alert("", t(`${PATH}/invalidOtp`));
      setOtp("");
      setSubmitting(false);
    }
  };

  if (showInstantCommissionModal) {
    return (
      <Modal
        visible={showInstantCommissionModal}
        presentationStyle="fullScreen"
        animationType="slide"
        statusBarTranslucent
        navigationBarTranslucent
      >
        <View
          style={[
            styles.modalContainer,
            { paddingTop: insets.top, paddingBottom: insets.bottom },
          ]}
        >
          <View style={styles.cross}>
            <TouchableOpacity
              testID="closeInstantCommissionModal"
              onPress={() => {
                analytics.trackEvent(RRD_EVENTS.COMISSION_MODAL_CLOSE, {
                  source: commissionEventsProps.header_cross,
                });
                if (afterOtpRedirectScreenName === "MyTask") {
                  navigation.pop(2);
                } else {
                  navigation.navigate(
                    afterOtpRedirectScreenName || "OrderDetails",
                    { order_id: id },
                  );
                }
              }}
            >
              <Cross width={20} height={20} />
            </TouchableOpacity>
          </View>
          <Image style={{ width: 142, height: 140 }} source={Success} />
          <Text style={styles.commissionText}>
            {commissionMsgs?.toastMessage ? commissionMsgs.toastMessage : ""}
          </Text>
          {commissionMsgs?.extra_commission_strip ? (
            <MetaStrip
              text={commissionMsgs.extra_commission_strip}
              icon="coin"
              type="BORDERED"
              extraStyling={{ width: "83%", marginBottom: 32 }}
              smallStrip
            />
          ) : null}
          <PrimaryButton
            onPress={() => {
              analytics.trackEvent(RRD_EVENTS.COMISSION_MODAL_CLOSE, {
                source: commissionEventsProps.primary_btn,
              });
              if (afterOtpRedirectScreenName === "MyTask") {
                navigation.pop(2);
              } else {
                navigation.navigate(
                  afterOtpRedirectScreenName || "OrderDetails",
                  { order_id: id },
                );
              }
            }}
            buttonColor={color.plain_green}
            text={t(`${PATH}/redirectionButton`)}
            textStyle={styles.modalButtonTitle}
            buttonStyle={styles.modalButton}
          />
        </View>
      </Modal>
    );
  }

  const displayPhoneNumbers =
    masked_phone_numbers && masked_phone_numbers.length
      ? masked_phone_numbers
      : phone_numbers;

  return (
    <View style={styles.container}>
      <HeaderWithBackAndTitle
        headertitle={t(`${PATH}/header`)}
        headerColor="#fff"
        hideShadow={false}
        navigation={navigation}
        route={route}
      />
      <Text
        newfont
        variant="body_text_3"
        style={{ padding: 16, color: color.lightBlack }}
      >
        {otpText}
      </Text>
      <OTPInput value={otp} valueLength={otpLength} onChange={onChange} />
      <PrimaryResendOTPLine
        {...{
          phone_numbers,
          masked_phone_numbers,
          showResend,
          resendOtp,
          selectedPhoneNumber,
          setShowNumberModal,
          order_id: id,
          cx_id,
          cp_db_id,
        }}
      />
      <SecondaryResendOTPLine
        {...{
          showResend,
          isCountDownRunning,
          countDown,
          setShowNumberModal,
          resendOtp,
          phone_numbers,
          order_id: id,
          cx_id,
          cp_db_id,
        }}
      />
      <PrimaryButton
        disabled={otp.trim().length !== otpLength}
        onPress={handleVerification}
        text={t(`${PATH}/verifyOTP`)}
        loading={isSubmitting}
        buttonStyle={styles.btn}
      />
      <OtpNumberModal
        visible={showNumberModal}
        onPress={resendOtp}
        onPressCancel={() => setShowNumberModal(false)}
        title="फ़ोन नंबर चुनें"
        phoneNumbers={phone_numbers}
        maskedPhoneNumbers={displayPhoneNumbers}
      />
    </View>
  );
}

export function OTPInput({ valueLength, value, onChange }) {
  const itemsRef = useRef([]);
  const valueItems = useMemo(() => {
    const valueArray = value.split("");
    const items = [];

    for (let i = 0; i < valueLength; i++) {
      const char = valueArray[i];

      if (/^\d+$/.test(char)) {
        items.push(char);
      } else {
        items.push("");
      }
    }

    return items;
  }, [value, valueLength]);

  const onKeyPress = (nativeEvent, idx) => {
    if (nativeEvent.key === "Backspace" && itemsRef.current[idx - 1]) {
      itemsRef.current[idx - 1].focus();
    }
  };

  const inputOnChange = (enteredValue, idx) => {
    let targetValue = enteredValue.trim();
    const isTargetValueDigit = /^\d+$/.test(targetValue);

    if (!isTargetValueDigit && targetValue !== "") {
      return;
    }

    targetValue = isTargetValueDigit ? targetValue : " ";

    const targetValueLength = targetValue.length;

    if (targetValueLength === 1) {
      const newValue =
        value.substring(0, idx) + targetValue + value.substring(idx + 1);
      onChange(newValue);

      if (!isTargetValueDigit) {
        return false;
      }
      if (itemsRef.current[idx + 1]) {
        itemsRef.current[idx + 1].focus();
      }
    } else if (targetValueLength === valueLength) {
      onChange(targetValue);
      itemsRef.current[valueLength - 1].blur();
    }
    onKeyPress(enteredValue, idx);
  };

  return (
    <View style={styles.enterPhone}>
      {valueItems.map((digit, index) => (
        <TextInput
          testID={`otpInput-${index}`}
          key={`input-${index}`}
          ref={(el) => (itemsRef.current[index] = el)}
          value={digit}
          style={styles.otpInput}
          maxLength={valueLength}
          keyboardType="number-pad"
          textContentType="oneTimeCode"
          autoComplete="sms-otp"
          onChangeText={(val) => inputOnChange(val, index)}
          onKeyPress={({ nativeEvent }) => onKeyPress(nativeEvent, index)}
        />
      ))}
    </View>
  );
}

function resendOtpHelper({
  phone_numbers,
  setShowNumberModal,
  resendOtp,
  order_id = "",
  cx_id = "",
  cp_db_id = "",
}) {
  if (!phone_numbers.length) return null;

  if (phone_numbers.length > 1) {
    setShowNumberModal(true);
    return analytics.trackEvent(RRD_EVENTS.DELIVERY_OTP_MODAL_OPEN, {
      order_id,
      cx_id,
      cp_db_id: cp_db_id || null,
    });
  }
  // single phone number case
  resendOtp(phone_numbers[0]);
}

function PrimaryResendOTPLine({
  phone_numbers,
  masked_phone_numbers,
  showResend,
  setShowNumberModal,
  resendOtp,
  selectedPhoneNumber,
  order_id = "",
  cx_id = "",
  cp_db_id = "",
}) {
  const displayPhoneNumbers =
    masked_phone_numbers && masked_phone_numbers.length
      ? masked_phone_numbers
      : phone_numbers;
  return !showResend ? (
    <Pressable
      style={{
        alignItems: "center",
        flexDirection: "row",
        justifyContent: "center",
      }}
      onPress={() => {
        resendOtpHelper({
          phone_numbers,
          setShowNumberModal,
          resendOtp,
          order_id,
          cx_id,
          cp_db_id,
        });
      }}
      testID="resendOtpSingleNumber"
    >
      <Text
        newfont
        variant="body_text_4"
        style={{
          paddingVertical: 16,
          paddingHorizontal: 2,
          color: color.blue,
          textDecorationLine: "underline",
        }}
      >{`मैसेज के माध्यम से OTP भेजें `}</Text>
      <Text
        newfont
        variant="body_text_4"
        style={{
          paddingVertical: 16,
          paddingHorizontal: 2,
          color: color.blue,
        }}
      >{`${
        displayPhoneNumbers.length
          ? displayPhoneNumbers.length > 1
            ? ""
            : `(फ़ोन : ${displayPhoneNumbers[0]})`
          : ""
      }`}</Text>
    </Pressable>
  ) : (
    <View style={{ alignItems: "center" }}>
      <Text
        newfont
        variant="body_text_3"
      >{`OTP ${selectedPhoneNumber} पर भेजा गया`}</Text>
    </View>
  );
}

function SecondaryResendOTPLine({
  showResend,
  isCountDownRunning,
  countDown,
  setShowNumberModal,
  phone_numbers,
  resendOtp,
  order_id,
  cx_id,
  cp_db_id,
}) {
  return showResend ? (
    <View style={{ flexDirection: "row", justifyContent: "center" }}>
      <Text
        newfont
        variant="body_text_3"
        style={{
          paddingVertical: 16,
          paddingHorizontal: 2,
          color: color.lightBlack,
        }}
      >
        OTP नहीं मिला?
      </Text>
      {isCountDownRunning ? (
        <Text
          newfont
          variant="body_text_2"
          style={{
            paddingVertical: 16,
            paddingHorizontal: 2,
            color: color.lightBlack,
          }}
        >
          दोबारा भेजें
        </Text>
      ) : null}
      <Pressable
        onPress={() => {
          if (isCountDownRunning) return;
          resendOtpHelper({
            phone_numbers,
            setShowNumberModal,
            resendOtp,
            order_id,
            cx_id,
            cp_db_id,
          });
        }}
        testID="resendOtpSingleNumber"
      >
        <Text
          newfont
          variant="body_text_2"
          style={{
            paddingVertical: 16,
            paddingHorizontal: 2,
            color: isCountDownRunning ? color.red : color.blue,
            textDecorationLine: "underline",
          }}
        >
          {isCountDownRunning ? `${countDown / 1000} sec` : "दोबारा भेजें"}
        </Text>
      </Pressable>
      {isCountDownRunning ? (
        <Text
          newfont
          variant="body_text_2"
          style={{
            paddingVertical: 16,
            paddingHorizontal: 2,
            color: color.lightBlack,
          }}
        >
          में
        </Text>
      ) : null}
    </View>
  ) : null;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.white,
  },
  otpInput: {
    borderColor: color.lighterBlack,
    borderRadius: 10,
    borderWidth: 1,
    paddingVertical: 8,
    paddingHorizontal: 16,
    minWidth: 50,
    fontSize: 16,
    minHeight: 40,
    textAlign: "center",
    marginRight: 10,
    width: 40,
    backgroundColor: color.lighterBlack,
    fontWeight: "bold",
  },
  enterPhone: {
    flexDirection: "row",
    padding: 20,
    width: "100%",
    justifyContent: "center",
  },
  btn: { margin: 20 },

  modalContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: color.lightGreenshade,
  },
  cross: {
    position: "absolute",
    top: 16,
    left: 16,
  },
  commissionText: {
    textAlign: "center",
    marginHorizontal: 20,
    fontSize: 20,
    fontWeight: "bold",
    color: color.black,
    marginTop: 20,
    marginBottom: 20,
  },
  modalButton: {
    width: "80%",
  },
  modalButtonTitle: {
    color: color.white,
    fontWeight: "bold",
    fontSize: 18,
  },
});

export default OTPVerification;
